import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { sanitize } from "../../lib/sanitize";
import { CheckCircle, XCircle, ArrowClockwise, FilePdf, Clock, Warning, UserCircle, WhatsappLogo } from "@phosphor-icons/react";
import { AttachmentGrid } from "../ui/AttachmentGrid";

const STATUS_DISPLAY = {
  pendiente: { icon: Clock, color: "text-amber-600", bg: "bg-amber-50", label: "Pendiente de Respuesta" },
  aprobado: { icon: CheckCircle, color: "text-green-600", bg: "bg-green-50", label: "Aprobado" },
  rechazado: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", label: "Rechazado" },
  vencido: { icon: Warning, color: "text-gray-500", bg: "bg-gray-50", label: "Vencido" },
};

const fmtMXN = (v) => (v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtUSD = (v) => (v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const mediaUrl = (path) => { if (!path) return ""; return `${API}/quotations/media/${path.replace(/^quotations\//, "")}`; };
const whatsappQR = (num) => `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(`https://wa.me/${num}`)}`;

export default function QuotationPublicView({ publicToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState("");
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: res } = await axios.get(`${API}/quotations/public/${publicToken}`);
        setData(res);
      } catch (e) { setError(e.response?.status === 404 ? "Cotizacion no encontrada" : "Error al cargar"); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [publicToken]);

  const handleRespond = async (action) => {
    setResponding(true);
    try {
      await axios.post(`${API}/quotations/public/${publicToken}/respond?action=${action}${comment ? `&comment=${encodeURIComponent(comment)}` : ""}`);
      setData(prev => ({ ...prev, quotation: { ...prev.quotation, status: action, client_comment: comment || null } }));
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    finally { setResponding(false); }
  };

  const handlePdf = async () => {
    try {
      const res = await axios.get(`${API}/quotations/public/${publicToken}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `cotizacion.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { alert("Error al descargar PDF"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><ArrowClockwise size={40} className="animate-spin text-blue-500" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-center"><XCircle size={48} className="mx-auto text-red-400 mb-3" /><p className="text-lg text-slate-600">{error}</p></div></div>;

  const q = data.quotation;
  const company = data.company || {};
  const resolvedTerms = data.resolved_terms || [];
  const sd = STATUS_DISPLAY[q.status] || STATUS_DISPLAY.pendiente;
  const StatusIcon = sd.icon;
  const hasDiscounts = q.global_discount_percent > 0 || (q.items || []).some(i => i.discount_percent > 0);
  const attachments = q.attachments || [];
  const managers = company.managers || [];
  const urgencyMessages = company.urgency_messages || [];
  const bannerImages = company.banner_images || [];
  const subtotalMxn = q.subtotal_mxn || q.total_mxn || 0;
  const subtotalUsd = q.subtotal_usd || q.total_usd || 0;
  const hasGlobalDiscount = (q.global_discount_percent || 0) > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white" style={{ fontFamily: "Inter, sans-serif" }} data-testid="public-quotation-view">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-5">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "Montserrat, sans-serif" }}>{company.company_name || "MaxiPOS"}</h1>
              {company.company_address && <p className="text-sm text-slate-500">{company.company_address}</p>}
              {company.company_phone && <p className="text-sm text-slate-500">Tel: {company.company_phone}</p>}
            </div>
            <div className="text-right">
              <p className="font-mono font-bold text-blue-600 text-lg">{q.quotation_number}</p>
              <p className="text-sm text-slate-400">{(q.created_at || "").slice(0, 10)}</p>
            </div>
          </div>

          {/* Status */}
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${sd.bg} mb-4`}>
            <StatusIcon size={24} className={sd.color} weight="fill" />
            <span className={`font-semibold ${sd.color}`}>{sd.label}</span>
          </div>

          {/* Validity + Urgency */}
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-xl p-4 text-white mb-4" data-testid="validity-banner">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Clock size={24} weight="fill" />
                <div>
                  <p className="font-bold text-base" style={{ fontFamily: "Montserrat, sans-serif" }}>VIGENCIA: {q.validity_days} DIAS</p>
                  {q.expires_at && <p className="text-xs text-white/80">Vence: {q.expires_at.slice(0, 10)}</p>}
                </div>
              </div>
              {urgencyMessages.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {urgencyMessages.map((msg, i) => (
                    <span key={`msg-${msg.slice(0,10)}-${i}`} className="px-2.5 py-1 bg-white/20 backdrop-blur rounded-full text-xs font-semibold flex items-center gap-1">
                      <Warning size={10} weight="fill" /> {msg}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Client + Creator */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <div className="sm:col-span-2">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1" style={{ fontFamily: "Montserrat, sans-serif" }}>Cliente</p>
              <p className="text-xl font-bold text-blue-800" style={{ fontFamily: "Montserrat, sans-serif" }}>{q.client_company || "Sin empresa"}</p>
              <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                <div><span className="text-slate-400">Encargado:</span> <b>{q.client_name}</b></div>
                {q.client_email && <div><span className="text-slate-400">Email:</span> {q.client_email}</div>}
                {q.client_phone && <div><span className="text-slate-400">Tel:</span> {q.client_phone}</div>}
              </div>
            </div>
            <div className="flex flex-col items-center text-center bg-slate-50 rounded-xl p-3" data-testid="creator-info">
              <p className="text-xs text-slate-400 mb-1">Asesor</p>
              {q.created_by_photo ? (
                <img src={mediaUrl(q.created_by_photo)} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-blue-200 mb-1" />
              ) : (
                <UserCircle size={56} className="text-blue-200 mb-1" weight="fill" />
              )}
              <p className="font-bold text-sm" style={{ fontFamily: "Montserrat, sans-serif" }}>{q.created_by_name}</p>
              {q.created_by_position && <p className="text-xs text-blue-600">{q.created_by_position}</p>}
              {q.created_by_email && <p className="text-xs text-slate-500">{q.created_by_email}</p>}
              {q.created_by_phone && <p className="text-xs text-slate-500">{q.created_by_phone}</p>}
            </div>
          </div>

          {/* Items table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm mb-4">
              <thead><tr className="bg-blue-600 text-white" style={{ fontFamily: "Montserrat, sans-serif" }}>
                <th className="px-3 py-2 text-left rounded-tl-lg">Producto</th>
                <th className="px-3 py-2 text-center">Cant.</th>
                <th className="px-3 py-2 text-right">P.U. MXN</th>
                <th className="px-3 py-2 text-right">P.U. USD</th>
                {hasDiscounts && <th className="px-3 py-2 text-center">Desc.</th>}
                <th className="px-3 py-2 text-right">Total MXN</th>
                <th className="px-3 py-2 text-right rounded-tr-lg">Total USD</th>
              </tr></thead>
              <tbody>
                {(q.items || []).map((item, i) => (
                  <tr key={item.id || `item-${i}`} className={`border-b ${i % 2 ? "bg-white" : "bg-blue-50/30"}`}>
                    <td className="px-3 py-2">
                      <span className="font-medium">{item.product_name || item.description}</span>
                      {item.description && item.product_name && <span className="block text-xs text-slate-400">{item.description}</span>}
                    </td>
                    <td className="px-3 py-2 text-center">{item.quantity}</td>
                    <td className="px-3 py-2 text-right">${fmtMXN(item.unit_price_mxn)}</td>
                    <td className="px-3 py-2 text-right">${fmtUSD(item.unit_price_usd)}</td>
                    {hasDiscounts && <td className="px-3 py-2 text-center text-orange-600">{item.discount_percent > 0 ? `${item.discount_percent}%` : "-"}</td>}
                    <td className="px-3 py-2 text-right font-semibold">${fmtMXN(item.total_mxn)}</td>
                    <td className="px-3 py-2 text-right font-semibold">${fmtUSD(item.total_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals - new labels */}
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-end gap-4">
              <span className="text-slate-500 font-semibold" style={{ fontFamily: "Montserrat, sans-serif" }}>Subtotal:</span>
              <span className="font-bold">${fmtMXN(subtotalMxn)} MXN</span>
              <span className="text-slate-400">${fmtUSD(subtotalUsd)} USD</span>
            </div>
            {hasGlobalDiscount && (
              <div className="flex justify-end gap-4 text-orange-600">
                <span className="font-medium italic">Oferta valida por tiempo limitado ({q.global_discount_percent}%):</span>
                <span className="font-bold">-${fmtMXN(q.global_discount_mxn)}</span>
              </div>
            )}
            {(q.taxes_detail || []).map((t, i) => (
              <div key={t.name || `tax-${i}`} className="flex justify-end gap-4 text-slate-600">
                <span>{t.name} ({t.rate}%):</span>
                <span>${fmtMXN(t.amount_mxn)}</span>
              </div>
            ))}

            {/* PRECIO ESPECIAL */}
            <div className="mt-3 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-5 text-white" data-testid="special-price">
              <p className="text-xs uppercase tracking-widest text-blue-200 mb-1" style={{ fontFamily: "Montserrat, sans-serif" }}>Precio Especial</p>
              <div className="flex items-end justify-between flex-wrap gap-2">
                <p className="text-4xl font-black" style={{ fontFamily: "Montserrat, sans-serif" }}>${fmtMXN(q.total_mxn)} <span className="text-base font-semibold text-blue-200">MXN</span></p>
                <p className="text-2xl font-bold text-green-300">${fmtUSD(q.total_usd)} <span className="text-sm text-green-200">USD</span></p>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-right">T.C.: 1 USD = ${q.exchange_rate} MXN</p>

          {/* Terms */}
          <div className="mt-5 pt-4 border-t text-sm">
            <p><b>Validez:</b> {q.validity_days} dias{q.expires_at ? ` (vence: ${q.expires_at.slice(0, 10)})` : ""}</p>
            {q.payment_terms && q.payment_terms !== "<p><br></p>" && (
              <div className="mt-2">
                <p className="font-semibold text-slate-700 mb-1">Forma de pago:</p>
                <div className="prose prose-sm max-w-none prose-p:my-1 prose-headings:my-1" dangerouslySetInnerHTML={sanitize(q.payment_terms)} />
              </div>
            )}
            {q.notes && q.notes !== "<p><br></p>" && (
              <div className="mt-2">
                <p className="font-semibold text-slate-700 mb-1">Notas:</p>
                <div className="prose prose-sm max-w-none prose-p:my-1 prose-headings:my-1" dangerouslySetInnerHTML={sanitize(q.notes)} />
              </div>
            )}
          </div>

          {/* Terms & Conditions */}
          {resolvedTerms.length > 0 && (
            <div className="mt-5 pt-4 border-t">
              <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Terminos y Condiciones</p>
              <div className="space-y-3">
                {resolvedTerms.map(tmpl => (
                  <div key={tmpl.id} className="rounded-lg border overflow-hidden">
                    <div className="px-3 py-2 text-xs font-semibold text-white" style={{ backgroundColor: tmpl.font_color || "#1E293B" }}>
                      {tmpl.name}
                    </div>
                    <div className="px-4 py-3 bg-slate-50/80 prose prose-sm max-w-none" style={{
                      color: tmpl.font_color || "#1E293B",
                      fontFamily: tmpl.font_family || "Helvetica, sans-serif",
                      fontSize: `${tmpl.font_size || 12}px`,
                      lineHeight: 1.5,
                    }} dangerouslySetInnerHTML={sanitize(tmpl.content || "")} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="mt-5 pt-4 border-t">
              <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Promociones y Recomendaciones</p>
              <AttachmentGrid attachments={attachments} apiBase={API} />
            </div>
          )}

          {/* PDF */}
          <div className="mt-4"><button onClick={handlePdf} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"><FilePdf size={18} /> Descargar PDF</button></div>
        </div>

        {/* Managers */}
        {managers.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border p-6" data-testid="managers-section">
            <p className="text-xs font-semibold text-slate-400 mb-4 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Nuestro Equipo - Contactanos</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {managers.map((mgr, i) => (
                <div key={mgr.id || mgr.name || `mgr-${i}`} className="flex gap-3 items-center bg-slate-50 rounded-xl p-4 border">
                  <div className="shrink-0">
                    {mgr.photo_path ? <img src={mediaUrl(mgr.photo_path)} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-blue-200" /> : <UserCircle size={56} className="text-slate-300" weight="fill" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm" style={{ fontFamily: "Montserrat, sans-serif" }}>{mgr.name}</p>
                    {mgr.position && <p className="text-xs text-blue-600">{mgr.position}</p>}
                    {mgr.email && <p className="text-xs text-slate-500">{mgr.email}</p>}
                    {mgr.phone && <p className="text-xs text-slate-500">{mgr.phone}</p>}
                  </div>
                  {mgr.whatsapp && (
                    <div className="shrink-0 flex flex-col items-center">
                      <img src={whatsappQR(mgr.whatsapp)} alt="QR" className="w-16 h-16 rounded border" />
                      <a href={`https://wa.me/${mgr.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-green-600 mt-1 hover:underline">
                        <WhatsappLogo size={10} weight="fill" /> WhatsApp
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Banners */}
        {bannerImages.length > 0 && (
          <div className="space-y-3" data-testid="banner-section">
            {bannerImages.map((b, i) => (
              <div key={b.id || b.path || `banner-${i}`} className="w-full rounded-xl overflow-hidden shadow-sm border">
                <img src={mediaUrl(b.path || b)} alt="" className="w-full h-auto max-h-48 object-cover" />
              </div>
            ))}
          </div>
        )}

        {/* Response section */}
        {q.status === "pendiente" && (
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <h3 className="font-semibold text-slate-700 mb-3" style={{ fontFamily: "Montserrat, sans-serif" }}>Tu respuesta</h3>
            <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Agregar un comentario (opcional)..."
              className="w-full px-3 py-2 border rounded-lg text-sm mb-4 min-h-[80px]" data-testid="public-comment" />
            <div className="flex gap-3">
              <button onClick={() => handleRespond("aprobado")} disabled={responding}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50" data-testid="approve-btn">
                <CheckCircle size={22} /> Aprobar Cotizacion
              </button>
              <button onClick={() => handleRespond("rechazado")} disabled={responding}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50" data-testid="reject-btn">
                <XCircle size={22} /> Rechazar
              </button>
            </div>
          </div>
        )}

        {(q.status === "aprobado" || q.status === "rechazado") && (
          <div className={`rounded-2xl border p-6 text-center ${q.status === "aprobado" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            <StatusIcon size={48} className={`mx-auto mb-2 ${sd.color}`} weight="fill" />
            <p className={`text-lg font-semibold ${sd.color}`} style={{ fontFamily: "Montserrat, sans-serif" }}>Esta cotizacion fue {q.status === "aprobado" ? "aprobada" : "rechazada"}</p>
            {q.client_comment && <p className="text-sm text-slate-500 mt-2">"{q.client_comment}"</p>}
          </div>
        )}
      </div>
    </div>
  );
}
